<script setup lang="ts">
import { useDark } from '@vueuse/core'
import { onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { setLocale } from '@/i18n'
import useFeatureFlags from '@/modules/shared/composables/use-feature-flags'
import { useAuthStore } from './modules/auth/stores/auth-store'

const route = useRoute()
const authStore = useAuthStore()
const { flags } = useFeatureFlags(['lang:en', 'lang:es', 'theme:dark'], { route })

///////////////////////////////////////////////////////////////////////////////
// Locale
///////////////////////////////////////////////////////////////////////////////

watch(flags, () => {
  if (flags['lang:en']) setLocale('en')
  if (flags['lang:es']) setLocale('es')
})

///////////////////////////////////////////////////////////////////////////////
// Theme
///////////////////////////////////////////////////////////////////////////////

const isDark = useDark()

// for now, always default to lightmode
isDark.value = false

watch(flags, () => {
  if (flags['theme:dark']) isDark.value = true
})

const intercomReady = ref(!!window.Intercom)

onMounted(() => {
  if (intercomReady.value) return
  // Intercom script not loaded yet — poll until it's available
  const interval = setInterval(() => {
    if (window.Intercom) {
      intercomReady.value = true
      clearInterval(interval)
    }
  }, 200)
  // Stop polling after 10 seconds
  setTimeout(() => clearInterval(interval), 10000)
})

let intercomBooted = false

const bootIntercom = () => {
  if (intercomBooted) return

  const appId = import.meta.env.VITE_INTERCOM_APP_ID
  if (!appId) return

  const hash = authStore.current_user.intercom_hash
  if (!hash || !intercomReady.value) return

  intercomBooted = true
  window.Intercom('boot', {
    api_base: 'https://api-iam.intercom.io',
    app_id: appId,
    email: authStore.current_user.email,
    user_id: authStore.current_user.id,
    user_hash: hash,
  })
}

watch(() => authStore.current_user.intercom_hash, bootIntercom, { immediate: true })
watch(intercomReady, bootIntercom)
</script>

<template>
  <RouterView :key="$route.fullPath" />
</template>
